import React, { useState, useEffect } from "react";
import { ref, get } from "firebase/database";
import { rtdb } from "../firebase";
import FGCard from "./FGCard";

export default function FgDetailModal({ date, items, onClose, onFgClick }) {
  const [hydratedItems, setHydratedItems] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(true);

  useEffect(() => {
    async function hydrateItems() {
      setLoadingMetadata(true);
      try {
        const promises = items.map(async (item) => {
          const receiveRef = ref(rtdb, `Receive/${item.fgNumber}`);
          const snapshot = await get(receiveRef);
          
          if (snapshot.exists()) {
            const extraData = snapshot.val();
            return {
              ...item,
              doNumber: extraData.doNumber || "N/A",
              labelType: extraData.labelType || "Unknown",
              quantity: extraData.quantity || 0
            };
          }
          return { ...item, doNumber: "N/A", labelType: "Unknown", quantity: 0 };
        });

        const resolved = await Promise.all(promises);
        setHydratedItems(resolved);
      } catch (err) {
        console.error("Error hydrating missing workflow cards:", err);
      } finally {
        setLoadingMetadata(false);
      }
    }

    if (items && items.length > 0) {
      hydrateItems();
    }
  }, [items]);

  const normalItems = hydratedItems.filter(item => !item.delayed);
  const delayedItems = hydratedItems.filter(item => item.delayed);
  const twoColumns = normalItems.length > 0 && delayedItems.length > 0;

  return (
    <div className="wow-modal-overlay">
      <div className="wow-modal-content fade-slide-up">
        
        {/* HEADER SECTION */}
        <div className="wow-modal-header">
          <div className="header-text-group">
            <span className="date-badge">{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
            <h3 className="wow-title">Station Overview</h3>
          </div>
          <button onClick={onClose} className="wow-close-btn" aria-label="Close Modal">✕</button>
        </div>

        {loadingMetadata ? (
          <div className="wow-loader-container">
            <span className="wow-spinner">🔄</span>
            Hydrating missing records...
          </div>
        ) : (
          /* CARDS GRID */
          <div className={`wow-grid ${twoColumns ? 'split-view' : 'single-view'}`}>

            {/* WIP COLUMN */}
            {normalItems.length > 0 && (
              <div className="wow-column">
                <div className="wow-column-header wip-header">
                  <span className="dot"></span>
                  <span className="label">Work In Progress</span>
                  <span className="count-badge">{normalItems.length}</span>
                </div>

                <div className="wow-card-stack">
                  {normalItems.map((item, index) => (
                    <div key={item.fgNumber} onClick={() => onFgClick(item.fgNumber)} className="wow-card-wrapper">
                      <FGCard item={item} isFirst={index === 0} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DELAYED COLUMN */}
            {delayedItems.length > 0 && (
              <div className="wow-column">
                <div className="wow-column-header delayed-header">
                  <span className="dot"></span>
                  <span className="label">Delayed Items</span>
                  <span className="count-badge">{delayedItems.length}</span>
                </div>

                <div className="wow-card-stack">
                  {delayedItems.map((item, index) => (
                    <div key={item.fgNumber} onClick={() => onFgClick(item.fgNumber)} className="wow-card-wrapper">
                      <FGCard item={item} isFirst={index === 0} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        /* 🛠️ FIX: Explicit dimensions & mobile alignment engine */
        .wow-modal-overlay { 
          position: fixed; 
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh; /* Utilizes dynamic viewport height to fight browser chrome layout shifting */
          background: rgba(15, 23, 42, 0.4); 
          backdrop-filter: blur(8px); 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          z-index: 2000; 
          padding: 16px; 
          box-sizing: border-box;
        }
        
        .wow-modal-content { 
          background: #f8fafc; 
          border: 1px solid #e2e8f0; 
          border-radius: 20px; 
          width: 100%; 
          max-width: ${twoColumns ? '850px' : '460px'}; 
          max-height: 85vh; 
          max-height: 85dvh; 
          overflow-y: auto; 
          box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.15); 
          position: relative; 
        }
        
        .wow-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: #2b4c7e; z-index: 5; }
        .date-badge { background: rgba(255, 255, 255, 0.2); color: white; padding: 4px 12px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
        .wow-title { margin: 4px 0 0 0; color: white; font-size: 20px; font-weight: 700; letter-spacing: -0.02em; }
        
        .wow-close-btn { background: rgba(255, 255, 255, 0.15); border: none; color: white; width: 44px; height: 44px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; transition: background 0.2s; }
        .wow-close-btn:hover { background: #ef4444; }
        
        .wow-grid { display: flex; gap: 24px; padding: 20px; }
        .split-view .wow-column { flex: 1; min-width: 280px; }
        .single-view .wow-column { width: 100%; }
        .wow-column-header { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; padding: 6px 14px; border-radius: 20px; width: fit-content; }
        .wow-column-header .dot { width: 6px; height: 6px; border-radius: 50%; }
        .wip-header { background-color: #d1fae5; }
        .wip-header .dot { background: #10b981; }
        .wip-header .label { color: #065f46; font-weight: 700; font-size: 13px; }
        .delayed-header { background-color: #fee2e2; }
        .delayed-header .dot { background: #f43f5e; }
        .delayed-header .label { color: #991b1b; font-weight: 700; font-size: 13px; }
        .count-badge { margin-left: 8px; background: rgba(15, 23, 42, 0.06); padding: 1px 8px; border-radius: 50px; color: #475569; font-size: 11px; font-weight: 700; }
        .wow-card-stack { display: flex; flex-direction: column; gap: 12px; }
        .wow-card-wrapper { cursor: pointer; }
        
        .wow-loader-container { padding: 40px; text-align: center; color: #475569; font-weight: 600; }
        .wow-spinner { display: inline-block; animation: spin 1s linear infinite; margin-right: 8px; }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .fade-slide-up { animation: fadeSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        
        /* 📱 Mobile UI Refinements */
        @media (max-width: 768px) {
          .wow-modal-overlay {
            align-items: center; /* Guarantees layout system anchors to dead center vertically */
            padding: 12px;       /* Adds a bit extra safety room for tighter phone aspect ratios */
          }
          .wow-grid { flex-direction: column; gap: 20px; padding: 16px; }
          .split-view .wow-column { min-width: 100%; }
          .wow-modal-content { 
            max-height: 85vh; 
            max-height: 85dvh; 
          }
          .wow-title { font-size: 18px; }
        }
        
        .wow-modal-content::-webkit-scrollbar { width: 6px; }
        .wow-modal-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>
    </div>
  );
}